import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { lightTheme, darkTheme } from '../styles/theme';
import { logError } from '../utils/errorHandling';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      return savedTheme === 'dark';
    } catch (error) {
      logError(error, 'ThemeProvider-Init');
      return false;
    }
  });

  const applyTheme = useCallback((theme) => {
    const root = document.documentElement;
    
    try {
      // Apply theme colors as CSS variables
      Object.entries(theme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });

      // Apply other theme properties
      root.style.setProperty('--font-family', theme.typography.fontFamily);
      
      // Update body styles
      document.body.style.backgroundColor = theme.colors['background-primary'];
      document.body.style.color = theme.colors['text-primary'];
      
      // Set data-theme attribute on body for CSS selectors
      document.body.setAttribute('data-theme', theme === darkTheme ? 'dark' : 'light');
      
      // Apply link styles directly to all links except navigation cards
      const links = document.querySelectorAll('a:not(.testCompleto):not(.modoPractica)');
      links.forEach(link => {
        // Skip links that are part of navigation cards
        if (!link.classList.contains('navButton')) {
          link.style.color = theme.colors['link-color'];
        }
      });
      
      // Apply link styles via CSS with exclusions for navigation cards
      const styleElement = document.getElementById('theme-link-styles') || document.createElement('style');
      styleElement.id = 'theme-link-styles';
      styleElement.textContent = `
        a:not(.navButton) { color: ${theme.colors['link-color']} !important; }
        a:not(.navButton):hover { color: ${theme.colors['link-hover-color']} !important; }
        
        /* Add high contrast text colors for navigation cards */
        a.navButton.testCompleto .title,
        a.navButton.testCompleto .description { color: #000080 !important; } /* Navy Blue for green background */
        
        a.navButton.modoPractica .title,
        a.navButton.modoPractica .description { color: #000080 !important; } /* Navy Blue for blue background */
        
        /* Dark mode styles */
        body[data-theme="dark"] a.navButton.testCompleto .title,
        body[data-theme="dark"] a.navButton.testCompleto .description,
        body[data-theme="dark"] a.navButton.modoPractica .title,
        body[data-theme="dark"] a.navButton.modoPractica .description { color: #ffffff !important; }
      `;
      if (!document.getElementById('theme-link-styles')) {
        document.head.appendChild(styleElement);
      }
      
      // Store theme preference
      localStorage.setItem('theme', theme === darkTheme ? 'dark' : 'light');
    } catch (error) {
      logError(error, 'ThemeProvider-ApplyTheme');
    }
  }, []);

  // Apply theme on mount and changes
  useEffect(() => {
    const theme = isDarkMode ? darkTheme : lightTheme;
    applyTheme(theme);
  }, [isDarkMode, applyTheme]);

  const toggleTheme = useCallback(() => {
    console.log('Theme toggled');
    setIsDarkMode(prev => {
      const newMode = !prev;
      console.log('Dark mode is now:', newMode);
      return newMode;
    });
  }, []);

  const value = {
    theme: isDarkMode ? darkTheme : lightTheme,
    isDarkMode,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;